<template>
    <div id="realTimeMap">
      <v-container fluid class="overrideContainer">
        <v-row>
          <v-col md="9" cols="12" lg="9" order="2" order-md="1" class="pl-4 pt-5 pr-4">
            <div>
              <v-expansion-panels>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    {{ $t('showMap')}}
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <template v-if="mapType !== null">
                        <GoogleRouteOptimizationComponent v-if="mapType == 1" />
                        <LeafletRouteOptimizationComponent v-if="mapType == 0"/>
                    </template>
                    
                    <div v-else>
                      Initializing map...
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
              
              <div v-show="dispatchNotes.length > 0" class="ml-3 mt-5">
                <v-data-table
                  :headers="tableHeaders"
                  :items="dispatchNotes.filter(x => !x.IsDriverComment)"
                  :hide-default-footer="true"
                  item-key="Pos"
                  class="custom-data-table"
                >
                  <template v-slot:item="{ item, index }">
                    <tr :class="{ 'draggable-item': index != dispatchNotes.length - 1, 'selected-row': index == selectedRouteIndex}" @click="handleRouteSelected(item, index)">
                      <td :data-label="$t('optimization.headers.Legend')"><div class="legendDiv" :style="{ backgroundColor: item.RouteColor }"></div></td>
                      <td :data-label="$t('optimization.headers.Pos')">{{ item.Code != undefined ? item.Pos : $t('optimization.returnRoute') }}</td>
                      <td class="d-none d-md-table-cell" :data-label="$t('optimization.headers.Latitude')">{{ item.Lat }}</td>
                      <td class="d-none d-md-table-cell" :data-label="$t('optimization.headers.Longitude')">{{ item.Lng }}</td>
                      <td :data-label="$t('optimization.headers.Code')">{{ item.Code }}</td>
                      <td :data-label="$t('optimization.headers.Weight')">{{ item.Weight }}</td>
                      <td :data-label="$t('comment')" @click.stop="showAddCommentDialog(item, index)">{{ item.Comment != undefined && item.Comment.length > 30 ? item.Comment.substring(0, 30).concat('...') : item.Comment }}</td>

                      <template v-if="!isUserDriverRole">
                        <td v-if="!item.IsComment" :data-label="$t('optimization.addComment')">
                          <v-btn color="primary" @click.stop="showAddCommentDialog(undefined, index)">{{ $t('optimization.addComment') }}</v-btn>
                        </td>
                        <td v-else :data-label="$t('action')">
                          <v-btn color="secondary" class="mr-5" @click.stop="deleteComment(index)">{{ $t('del') }}</v-btn>
                          <v-btn color="primary" @click.stop="showAddCommentDialog(item, index)">{{ $t('show') }}</v-btn>
                        </td>
                      </template>
                      
                      <td class="checkbox" :data-label="$t('optimization.headers.IsDelivered')"><v-checkbox class="mt-3" v-model="item.IsDelivered" :disabled="isDeliveredCheckboxDisabled" @click.stop="toggleIsDelivered(item, index)"></v-checkbox></td>
                    </tr>
                  </template>
                </v-data-table>

                <div v-if="isUserDriverRole && showSaveButton" class="mt-5 mr-3 text-right">
                  <v-btn color="success" small @click="saveRouteChanges">{{ $t('save') }}</v-btn>
                </div>

                <div class="mt-5">
                  <v-container>
                    <v-row>
                      <v-col md="4" sm="12">
                        <v-card class="py-3">
                          <v-card-title>
                            <span class="headline">{{ $t("driverComment") }}</span>
                          </v-card-title>
                          <v-card-text>
                            <v-textarea v-model="driverComment" :label="$t('comment')" :disabled="!isUserDriverRole" />
                          </v-card-text>
                          <v-card-actions v-if="isUserDriverRole">
                            <v-spacer></v-spacer>
                            <v-btn color="secondary" @click="() => { showCommentDialog = false, this.comment = '' }">{{ $t("cancel") }}</v-btn>
                            <v-btn color="primary" @click="saveComment">{{ $t("save") }}</v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-container>
                </div>

                <v-dialog v-model="showCommentDialog" max-width="500px" @input="commentDialogDismissed">
                  <v-card>
                    <v-card-title>
                      <span class="headline">{{ $t("comment") }}</span>
                    </v-card-title>
                    <v-card-text>
                      <v-container>
                        <v-row>
                          <v-col cols="12">
                            <v-textarea v-model="comment" :label="$t('comment')" :disabled="isUserDriverRole"  />
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-card-text>
                    <v-card-actions v-if="!isUserDriverRole">
                      <v-spacer></v-spacer>
                      <v-btn color="secondary" @click="() => { showCommentDialog = false, this.comment = '' }">{{ $t("cancel") }}</v-btn>
                      <v-btn color="primary" @click="saveComment">{{ $t("save") }}</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>

              </div>

            </div>
          </v-col>
          <v-col cols="12" md="3" lg="3" order="1" order-md="2">
             <v-row no-gutters class="pl-4 pt-3 pr-4">
                 <v-col cols="12">
                  <v-date-field :label="$t('optimization.optimizedFor')" v-model="selectedDate" @input="selectedDateChanged" :clear-content="true"></v-date-field>
                   
                  <v-autocomplete v-if="!isUserDriverRole" multiple outlined dense hide-details :items="drivers" v-model="selectedDrivers" :label="$t('drivers')" item-text="name" item-value="id" />

                  <v-autocomplete outlined dense hide-details :items="routeOptimizations" v-model="selectedOptimization" :label="$t('optimization.optimizations')" item-text="name" item-value="id"/>

                  <v-text-field :error-messages="routeOptimizationNameError" outlined dense v-model="routeOptimizationName" :label="$t('optimization.name')" @input="handleRouteOptimizationNameChange"></v-text-field>

                  <v-simple-table v-if="!isUserDriverRole && vehicles.length > 0" dense class="font-weight-light mt-3" single-select>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th>{{ $t('vehicle') }}</th>
                          <th>{{ $t('action') }}</th>
                          <th>{{ $t('passengerDriver') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="vehicle in vehicles" :class="{ 'selected-row': selectedVehicle != null && vehicle.Reg == selectedVehicle.Reg }">
                          <td>{{ vehicle.Reg }}</td>
                          <td><v-btn color="success" class="my-3" small @click="handleSelectedVehicle(vehicle)">{{ $t('select') }}</v-btn></td>
                          <td>
                            <v-autocomplete outlined dense hide-details :items="driversPassengerCrew" v-model="vehicle.PassengerDriverId" :label="$t('passengerDriver')" item-text="name" item-value="id" @change="bindPassengerDriverIdProperty($event, vehicle)" />
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>

                 </v-col>

                 <v-col v-if="!isUserDriverRole" cols="12" class="mt-5">
                   <v-btn v-if="selectedOptimization && showSaveButton" class="float-right" color="success" small @click="saveRouteChanges">{{ $t('save') }}</v-btn>
                   <v-btn v-if="selectedOptimization" class="float-right mr-3" color="warning" small @click="deleteOptimization">{{ $t('del') }}</v-btn>
                 </v-col>
 
             </v-row>
              
          </v-col>

        </v-row>
      </v-container>
    </div>
  </template>
  
<script>
import { mapState } from 'vuex'
import store from '@/store/store'
import mapMixin from '../mapMixin.js'
import httpClient from '@/api/httpClient'
import LocationFinder from './LocationFinder'
import Sortable from 'sortablejs'

export default {
  name: 'BaseRouteOptimizationWeightComponent',
  data() {
    return {
      routeOptimizations: [],
      selectedOptimization: undefined,
      drivers: [],
      driversPassengerCrew: [],
      selected: [],
      selectedDrivers: [],
      selectedDate: undefined,
      vehicles: [],
      selectedVehicle: undefined,
      tableHeaders: [
        {
          text: this.$t('optimization.headers.Legend'),
          align: 'start',
          sortable: false,
          value: ''
        },
        {
          text: this.$t('optimization.headers.Pos'),
          align: 'start',
          sortable: false,
          value: 'Pos'
        },
        {
          text: this.$t('optimization.headers.Latitude'),
          align: 'start',
          sortable: false,
          value: 'Lat',
          class: 'd-none d-md-table-cell'
        },
        {
          text: this.$t('optimization.headers.Longitude'),
          align: 'start',
          sortable: false,
          value: 'Lng',
          class: 'd-none d-md-table-cell'
        },
        {
          text: this.$t('optimization.headers.Code'),
          align: 'start',
          sortable: false,
          value: 'Code'
        },
        {
          text: this.$t('optimization.headers.Weight'),
          align: 'start',
          sortable: false,
          value: 'Weight'
        },
        {
          text: this.$t('comment'),
          align: 'start',
          sortable: false,
          value: 'Comment'
        },
        {
          text: this.$t('optimization.headers.IsDelivered'),
          align: 'start',
          sortable: false,
          value: 'isDelivered'
        },
      ],
      showSaveButton: false,
      updatedDispatchNotes: [],
      selectedRouteIndex: undefined,
      comment: '',
      showCommentDialog: false,
      isCommentUpdate: false,
      isOptimizationFinished: false,
      driverComment: '',
      routeOptimizationName: '',
      initialrouteOptimizationName: '',
      routeOptimizationNameError: []
    }
  },
  mixins: [mapMixin],
  components: {
    LeafletRouteOptimizationComponent: () =>
      import('./LeafletRouteOptimization'),
    GoogleRouteOptimizationComponent: () => import('./GoogleRouteOptimization'),
    LocationFinder,
  },
  mounted() {

    if (!this.isUserDriverRole) {
      this.tableHeaders.splice(this.tableHeaders.length - 2, 0,
        {
          text: this.$t('action'),
          align: 'start',
          sortable: false
        }
      )

      this.loadDrivers()
      this.initSortable()
    }
  },
  beforeDestroy() {
    this.clearSelection()
  },
  computed: {
    ...mapState('optimization', ['dispatchNotes', 'source', 'endPoint']),
    ...mapState('user', ['userDecoded', 'isUserDriverRole']),
    isDeliveredCheckboxDisabled() {
      if (!this.isUserDriverRole) {
        return true
      }

      const optimization = this.routeOptimizations.filter(
          x => x.id == this.selectedOptimization
        )[0]

      let currentDate = new Date()
      let optimizationDate = new Date(optimization.optimizedFor)

      if (currentDate.getFullYear() != optimizationDate.getFullYear() || !this.isUserDriverRole) {
        return true
      }

      return this.isOptimizationFinished && currentDate.getDate() != optimizationDate.getDate() && currentDate.getMonth() != optimizationDate.getMonth()
    }
  },
  methods: {
    clearSelection() {
      store.dispatch('optimization/clearSelection')
      store.dispatch('optimization/clearDestinations')
      store.dispatch('optimization/setRoutes', { routes: [], markers: [] })
      store.dispatch('optimization/setSource', undefined)
      store.dispatch('optimization/setEndPoint', undefined)
    },
    reset() {
      store.dispatch('optimization/reset')
    },
    deleteOptimization() {
      httpClient
        .delete('routeoptimization/' + this.selectedOptimization)
        .then(() => {
          this.selectedOptimization = undefined
          this.vehicles = []
          this.loadRouteOptimizations()
        })
    },
    loadRouteOptimizations() {
      let url = '/routeoptimization/WeightRoutes?type=2'
      if (this.isUserDriverRole) {
        let user = JSON.parse(this.userDecoded.UserData)
        let driverIds = [user.DriverData.DriverId]
        url += `&driverIds=${driverIds.join(',')}`
      }
      
      if (this.selectedDate) {
        url += `&optimizedFor=${this.selectedDate}`
      }

      httpClient.get(url).then(result => {
        this.routeOptimizations = result.data
      })
    },
    async loadDrivers() {
      await httpClient.get('/drivers')
        .then(result => {
          result.data.unshift({
            name: 'Izaberite...',
            id: null
          })
          this.drivers = result.data
      })
    },
    loadPassengerCrew() {
      this.driversPassengerCrew = this.drivers.filter(x => !this.selectedDrivers.includes(x.id))
    },
    selectedDateChanged() {
      this.loadRouteOptimizations()
      this.vehicles = []
    },
    initSortable() {
      let table = document.querySelector("tbody")
      const that = this
      
      Sortable.create(table, {
        draggable: '.draggable-item',
        onEnd(event) {
          if (that.updatedDispatchNotes.length < 1) {
            that.updatedDispatchNotes = [...[], ...that.dispatchNotes]
          }

          that.showSaveButton = true
          that.updatedDispatchNotes.splice(
            event.newIndex,
            0,
            ...that.updatedDispatchNotes.splice(event.oldIndex, 1)
          );

        }
      });
    },
    handleRouteOptimizationNameChange(value) {
      if (this.initialrouteOptimizationName != value) {
        this.showSaveButton = true
      } else {
        this.showSaveButton = false
      }
    },
    saveRouteChanges() {
      let optimization = this.routeOptimizations.find(
          x => x.id == this.selectedOptimization
        )
        
      let shouldRefreshOptimizations = false

      if (this.selectedVehicle && this.updatedDispatchNotes.length > 0) {
        let routeData = JSON.parse(optimization.routeData)
        let vehicleData = routeData.Vehicles.find(v => v.Reg == this.selectedVehicle.Reg)
        vehicleData.DispatchNotes = this.updatedDispatchNotes

        optimization.routeData = JSON.stringify(routeData);
        shouldRefreshOptimizations = true
      }

      optimization.name = this.routeOptimizationName

      httpClient
        .put('/routeoptimization/' + this.selectedOptimization, optimization)
        .then(() => {
          if (shouldRefreshOptimizations) {
            this.loadRouteOptimizations()
          }

          store.dispatch(
            'notification/add',
            { type: 'success', message: this.$t('apiSuccessMsg') },
            { root: true }
          )
        })
        .catch(error => {
          if (error.response.status == 422) {
            this.routeOptimizationNameError = [error.response.data.errors[0].Error]
          }
        })
      
      this.checkAllDispatchNotesDelivered()
    },
    handleSelectedVehicle(vehicle) {
      this.selectedVehicle = vehicle
    },
    handleRouteSelected(dispatchNote, index) {
      this.selectedRouteIndex = index
      store.dispatch(
        'optimization/setSelectedRoute', 
        { route: dispatchNote.Route }
      )
    },
    bindPassengerDriverIdProperty(selectedDriverId, vehicle) {
      if (!vehicle.hasOwnProperty('PassengerDriverId')) {
        this.$set(vehicle, 'PassengerDriverId', selectedDriverId);
      } else {
        vehicle.PassengerDriverId = selectedDriverId;
      }

      let optimization = this.routeOptimizations.find(
        x => x.id == this.selectedOptimization
      )

      if (optimization) {
        let routeData = JSON.parse(optimization.routeData)
        routeData.Vehicles = this.vehicles
        optimization.routeData = JSON.stringify(routeData)
      }

      this.showSaveButton = true
    },
    showAddCommentDialog(item, index) {
      if (item) {
        this.comment = item.Comment
        this.isCommentUpdate = true
      } else {
        this.isCommentUpdate = false
      }

      this.showCommentDialog = true
      this.selectedRouteIndex = index
    },
    saveComment() {
      if (!this.isUserDriverRole) {
        if (!this.isCommentUpdate) {
          this.insertNewComment(this.comment, this.selectedRouteIndex)
        } else {
          this.updateComment(this.comment, this.selectedRouteIndex)
        }
      } else {
        let isDriverCommentExist = this.dispatchNotes.some(x => x.IsDriverComment);
        if (isDriverCommentExist) {
          let driverCommentIndex = this.dispatchNotes.findIndex(x => x.IsDriverComment);
          this.updateComment(this.driverComment, driverCommentIndex)
        } else {
          this.insertNewComment(this.driverComment, this.dispatchNotes.length)
        }
      }
      
      this.updatedDispatchNotes = [...[], ...this.dispatchNotes]
      this.comment = ''

      this.showCommentDialog = false

      this.showSaveButton = true
    },
    insertNewComment(comment, index) {
      this.dispatchNotes.splice(index + 1, 0, { Comment: comment, Code: '', IsComment: true, IsDriverComment: this.isUserDriverRole })
    },
    updateComment(comment, index) {
      let dispatchNote = this.dispatchNotes[index]
      dispatchNote.Comment = comment;
      this.dispatchNotes[index] = {...dispatchNote}
    },
    deleteComment(index) {
      this.dispatchNotes.splice(index, 1)
      this.updatedDispatchNotes = [...[], ...this.dispatchNotes]

      this.showSaveButton = true
    },
    commentDialogDismissed() {
      this.comment = ''
    },
    toggleIsDelivered(item, index) {
      if (this.isOptimizationFinished) {
        return
      }

      item.IsDelivered = !item.IsDelivered
      item.DeliveredAt = item.DeliveredAt ? new Date().toISOString() : null
      item.DeliveryStatus = item.IsDelivered ? 3 : 2

      this.$set(this.dispatchNotes, index, {
        ...item,
      })
      
      this.updatedDispatchNotes = [...[], ...this.dispatchNotes]

      this.showSaveButton = true
    },
    checkAllDispatchNotesDelivered() {
      this.isOptimizationFinished = this.dispatchNotes.every(note => note.IsDelivered);
    },
  },
  watch: {
    selectedOptimization: {
      handler() {
        this.clearSelection()
        if (!this.selectedOptimization) {
          return
        }

        const optimization = this.routeOptimizations.filter(
          x => x.id == this.selectedOptimization
        )[0]

        this.routeOptimizationName = optimization.name
        this.initialrouteOptimizationName = optimization.name
        this.selectedDrivers = optimization.driverIds
        this.loadPassengerCrew()
        const routeData = JSON.parse(optimization.routeData)

        this.vehicles = routeData.Vehicles

        if (this.isUserDriverRole) {
          let user = JSON.parse(this.userDecoded.UserData)
          let vehicle = routeData.Vehicles.find(x => x.Reg == user.DriverData.RegistrationMark)
          this.selectedVehicle = vehicle
        }
      }
    },
    selectedVehicle: {
      handler() {
        let routeData = {
          routes: [],
          markers: []
        }

        for (let i = 0; i < this.selectedVehicle.DispatchNotes.length; i++) {
          let dispatchNote = this.selectedVehicle.DispatchNotes[i]
          if (!dispatchNote.Route) {
            continue
          }

          routeData.markers.push({ latitude: dispatchNote.Lat, longitude: dispatchNote.Lng, desc: dispatchNote.Pos });
          routeData.routes.push({ coordinates: dispatchNote.Route, color: dispatchNote.RouteColor, width: 0, visible: true})
        }

        store.dispatch('optimization/setRoutes', routeData)

        store.dispatch('optimization/clearDestinations')
        routeData.markers.forEach(marker => {
          store.dispatch('optimization/addDestination', {
            latlng: {
              lat: marker.latitude,
              lng: marker.longitude
            }
          })
        })

        let sourcePoint = {
          latlng: {
            lat: routeData.routes[0].coordinates[0][0],
            lng: routeData.routes[0].coordinates[0][1]
          }
        }

        if (!this.source || !this.source.latlng || (this.source.latlng.lat != sourcePoint.latlng.lat || this.source.latlng.lng != sourcePoint.latlng.lng)) {
          store.dispatch('optimization/setSource', sourcePoint)
        }

        let endRoute = routeData.routes[routeData.routes.length - 1];
        let endPoint = {
          latlng: {
            lat: endRoute.coordinates[endRoute.coordinates.length - 1][0],
            lng: endRoute.coordinates[endRoute.coordinates.length - 1][1]
          }
        };

        if (!this.endPoint || !this.endPoint.latlng || (endPoint.latlng.lat != this.endPoint.latlng.lat || endPoint.latlng.lng != this.endPoint.latlng.lng)) {
          store.dispatch('optimization/setEndPoint', endPoint)
        }

        store.dispatch('optimization/setDispatchNotes', this.selectedVehicle.DispatchNotes)

        this.checkAllDispatchNotesDelivered()

        var driverCommentExist = this.dispatchNotes.some(x => x.IsDriverComment);
        if (driverCommentExist) {
          this.driverComment = this.dispatchNotes.find(x => x.IsDriverComment).Comment
        }

      }
    },
  }
}
</script>
  
<style>
.overrideContainer {
  padding: 0px !important;
}

.drag-handle {
  cursor: grab;
}

.v-data-table-header span {
  color: rgba(0, 0, 0, 0.6) !important; 
}

.legendDiv {
  width: 30px;
  height: 30px;
}

.selected-row {
  background-color: rgb(238, 238, 238);
}

.v-expansion-panel-content__wrap {
  padding: 0px !important;
}

.custom-data-table .v-data-table-header {
  display: table-header-group;
}

.custom-data-table tr {
  display: table-row;
}

.custom-data-table td {
  display: table-cell;
  padding: 8px;
}

@media (max-width: 768px) {
  .custom-data-table tr {
    display: block;
    margin-bottom: 10px;
    border: 1px solid #e0e0e0;
    padding: 10px;
    background: #fff;
  }

  .custom-data-table .v-data-table-header {
    display: none; 
  }

  .custom-data-table td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: thin solid rgba(0, 0, 0, 0.12);
    width: 100%;
  }

  .custom-data-table td:before {
    content: attr(data-label);
    flex: 1;
    font-weight: bold;
    padding-right: 10px;
  }

  .custom-data-table .legend {
    order: -1;
    display: block;
  }
}
</style>
  